import type { LifecyclePushVariantRow } from '@/types';

/** The backend's ceiling on the lifecycle push daily cap. */
export const MAX_DAILY_CAP = 500_000;

/** "Came back within 24 h" as a share of delivered pushes; "—" when none were delivered. */
export function cameBackRate(returned24h: number, delivered: number): string {
  if (!delivered) return '—';
  return `${((returned24h / delivered) * 100).toFixed(1)}%`;
}

/**
 * Whether this admin may set the daily cap to `next`: a whole number from 0 to
 * 500,000, and no higher than today's cap unless they may raise it (super
 * admin). The backend checks the same.
 */
export function canEditCap(next: number, current: number, canRaise: boolean): boolean {
  if (!Number.isInteger(next) || next < 0 || next > MAX_DAILY_CAP) return false;
  return next <= current || canRaise;
}

/** The control copy keeps today's English text; every other copy is new. */
const CONTROL = 'control';
const CONTROL_LABEL = "control (today's copy)";

/** `<trigger>.<segment>.<language|control>`, or null when the string is not that shape. */
function parseVariant(variant: string): { trigger: string; segment: string; last: string } | null {
  const parts = variant.split('.');
  if (parts.length !== 3 || parts.some((p) => !p)) return null;
  return { trigger: parts[0], segment: parts[1], last: parts[2] };
}

/** The push of this trigger carries no tap target, so its key figure is the tutorial. */
export const TUTORIAL_TRIGGER = 'ftue_incomplete';

/**
 * Whether a copy row is about the tutorial, so "Tutorial done" applies: the
 * Left mid-tutorial trigger, or the `tutorial` segment of any trigger (also
 * summed, `<trigger>.tutorial`).
 */
export function isTutorialRow(trigger: string, variant: string | null): boolean {
  if (trigger === TUTORIAL_TRIGGER) return true;
  if (variant === null) return false;
  const parts = variant.split('.');
  return (parts.length === 2 || parts.length === 3) && parts[1] === 'tutorial';
}

/**
 * Reader-facing name of a copy: `lost_last · hi`, `lost_last · control (today's
 * copy)`, "before variants" for pushes sent before copies existed, and the raw
 * string for anything else. With `summed` a `<trigger>.<segment>` row (see
 * groupVariantsBySegment) reads `lost_last · all languages`.
 */
export function variantLabel(variant: string | null, summed = false): string {
  if (variant === null) return 'before variants';
  const parsed = parseVariant(variant);
  if (parsed) {
    return `${parsed.segment} · ${parsed.last === CONTROL ? CONTROL_LABEL : parsed.last}`;
  }
  const parts = variant.split('.');
  if (summed && parts.length === 2 && parts.every(Boolean)) return `${parts[1]} · all languages`;
  return variant;
}

/**
 * Sums the per-language copy rows by `<trigger>.<segment>` so a segment's new
 * copy can be read against its control, which stays its own row
 * (`<trigger>.<segment>.control`). Rows with no variant are summed per trigger;
 * an unparseable variant is kept as it is. Most delivered first.
 */
export function groupVariantsBySegment(rows: LifecyclePushVariantRow[]): LifecyclePushVariantRow[] {
  const groups = new Map<string, LifecyclePushVariantRow>();
  for (const row of rows) {
    const parsed = row.variant === null ? null : parseVariant(row.variant);
    let variant = row.variant;
    if (parsed) {
      variant = parsed.last === CONTROL ? row.variant : `${parsed.trigger}.${parsed.segment}`;
    }
    const key = `${row.trigger}|${variant}`;
    const sum = groups.get(key);
    if (!sum) {
      groups.set(key, { ...row, variant, language: null });
      continue;
    }
    sum.sent += row.sent;
    sum.delivered += row.delivered;
    sum.returned2h += row.returned2h;
    sum.returned24h += row.returned24h;
    sum.matchStarted24h += row.matchStarted24h;
    sum.returnedNoMatchPush24h += row.returnedNoMatchPush24h;
    sum.tutorialDone24h += row.tutorialDone24h;
  }
  return [...groups.values()].sort((a, b) => b.delivered - a.delivered);
}
